import { useMutation, useQuery } from '@apollo/client/react';
import { chakra, Alert, Box, Button, Center, Separator, Heading, HStack, Link, Spinner, Text, VStack } from '@chakra-ui/react';
import { CouponForm } from '@src/components/sco/Account/SignupPaywall/CouponForm';
import { StripePaymentMethod } from '@src/components/sco/Account/StripePaymentMethodForm';
import { UserBasicProfileForm } from '@src/components/sco/Account/UserBasicProfileForm';
import { UserLoginForm } from '@src/components/sco/Account/UserLoginForm';
import { UserSignupRegisterForm, UserSignupRegisterFormProps } from '@src/components/sco/Account/UserSignupRegisterForm';
import { GenericFormProps } from '@src/components/sco/Form/formHelpers';
import { FormContainer } from '@src/components/sco/FormContainer';
import { CheckCircleIcon } from '@src/components/sco/icons';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ScoProductPricing, ScoProductTerm, ScoPurchaseQuote, SubmitCheckoutPayload } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { TrackEvent } from '@src/lib/TrackEvent';
import { renderCurrency } from '@src/lib/adminUtils';
import { gql } from '@src/lib/gql';
import { prettyErrorMessage } from '@src/lib/mutationErrors';
import { startupsPath } from '@src/lib/startups-url';
import tracker from '@src/lib/tracker';
import { PaymentMethod, Stripe } from '@stripe/stripe-js';
import moment, { Moment } from 'moment';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { LuChevronRight } from 'react-icons/lu';
const pluralize = require('pluralize');
export type SignupPaymentFormProps = GenericFormProps<any> & {
  additionalHubspotFormKey?: string;
  afterError?: (checkoutResponse?: SubmitCheckoutPayload, error?: any) => any;
  afterSuccess?: (checkoutResponse: SubmitCheckoutPayload) => any;
  allowProductChange?: boolean;
  couponCode?: string;
  hideProductTitle?: boolean;
  hideProductTitleTerm?: boolean;
  productTitle?: string;
  hidePricing?: boolean;
  hubspotFormKey?: string;
  /** Conversion intent attached to checkout analytics. */
  intent?: string | null;
  onSubmit?: () => any;
  paymentProvider?: string | 'stripe-methods' | 'no-card';
  productId: string;
  productChangePath?: string;
  returnToHref?: string;
  showCouponForm?: boolean;
  startsAt?: Date | Moment;
  termId?: string;
  title?: string;
  registerFormProps?: UserSignupRegisterFormProps;
};
export const SignupPaymentForm = (props: SignupPaymentFormProps) => {
  const router = useRouter();
  const queryParams = useSearchParams();
  const {
    currentUser,
    refetchCurrentUser
  } = useCurrentUser();
  const [paymentProvider, setPaymentProvider] = useState<SignupPaymentFormProps['paymentProvider']>(props.paymentProvider || 'stripe-methods');
  const [bypassProfileConfirmation, setBypassProfileConfirmation] = useState<boolean>(false);
  const [stripeInstance, setStripeInstance] = useState<Stripe>(null);
  const [stripePaymentMethod, setStripePaymentMethod] = useState<PaymentMethod>(null);
  const [processing, setProcessing] = useState<boolean>(false);
  const [processingMessage, setProcessingMessage] = useState<string>('Processing...');
  const [success, setSuccess] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [registerFormType, setRegisterFormType] = useState<string>('register');
  const [paymentIsRequired, setPaymentIsRequired] = useState<boolean>(true);
  const stripePaymentIntentId = queryParams.get('payment_intent') || null;
  const quoteQuery = useQuery(gql`
      query ($productId: String!, $couponCode: String, $termId: String) {
        scoPurchaseQuote(productId: $productId, couponCode: $couponCode, termId: $termId) {
          id
          product {
            id
            appliedCoupon {
              code
            }
            paywallOptions {
              title
              description
            }
            effectiveTermId
            effectiveTerm {
              termId
              abbreviation
              everyMonthLabel
              frequencyLabel
              name
            }
            effectiveLegacyProductId
            pricingInCents {
              prepaidPeriodRequiresCard
              prepaidPeriods
              prepaidPeriodsUnit
              recurring
              signup
              trialDays
              trialRequiresCard
            }
            effectivePricingInCents {
              nextAssessmentAt
              prepaidPeriodRequiresCard
              prepaidPeriods
              prepaidPeriodsUnit
              recurring
              signup
              trialDays
              trialRequiresCard
            }
            productId
            specialTermsNote
            instanceProductId
            statementOfWorkUrl
            title
          }
        }
      }
    `, {
    fetchPolicy: 'network-only',
    ssr: false,
    variables: {
      productId: props.productId,
      termId: props.termId,
      couponCode: props.couponCode
    }
  });
  const quote: ScoPurchaseQuote = quoteQuery.data?.scoPurchaseQuote;
  const listPricing: ScoProductPricing = quoteQuery.data?.scoPurchaseQuote?.product?.pricingInCents || {};
  const effectivePricing: ScoProductPricing = quoteQuery.data?.scoPurchaseQuote?.product?.effectivePricingInCents || {};
  const effectiveTerm: ScoProductTerm = quoteQuery.data?.scoPurchaseQuote?.product?.effectiveTerm || {};
  const effectiveLegacyProductId = quote?.product?.effectiveLegacyProductId;
  const [submitCheckout] = useMutation(gql`
    mutation ($input: SubmitCheckoutInput!) {
      submitCheckout(input: $input) {
        chargebeeDataAsJson
        chargebeePaymentIntentId
        nextActionUrl
        stripeDataAsJson
        stripePaymentIntentId
        stripePaymentIntentStatus
        subscription {
          id
          product {
            appliedCoupon {
              code
            }
            effectivePrices {
              id
              recurring
              signup
            }
            listPrices {
              id
              recurring
              signup
            }
            name
            productId
          }
          subscriptionId
        }
      }
    }
  `);
  useEffect(() => {
    if (stripePaymentIntentId) {
      handleCheckoutSubmit({
        stripePaymentIntentId: stripePaymentIntentId
      });
    }
  }, [stripePaymentIntentId]);
  useEffect(() => {
    setErrorMessage(null);
  }, [props.productId, props.couponCode, props.termId]);
  useEffect(() => {
    if (effectivePricing.signup === 0 && effectivePricing.recurring === 0 || effectivePricing.trialDays && !effectivePricing.trialRequiresCard) {
      setPaymentIsRequired(false);
    } else {
      setPaymentIsRequired(true);
    }
  }, [effectivePricing.signup, effectivePricing.recurring, effectivePricing.trialDays, effectivePricing.trialRequiresCard]);
  useEffect(() => {
    if (stripePaymentMethod?.id) {
      handleCheckoutSubmit();
    }
  }, [stripePaymentMethod]);
  useEffect(() => {
    if (!processing) {
      setProcessingMessage('Processing...');
    }
  }, [processing]);
  const handleCheckoutSubmit = async (variables = null) => {
    await tracker.paymentInfoEntered({
      paymentMethod: 'card'
    });
    setProcessing(true);
    if (variables) {
      setProcessingMessage('Setting up Your Account...');
    }
    setErrorMessage(null);
    let checkoutResponse = null;
    try {
      checkoutResponse = await submitCheckout({
        variables: {
          input: {
            additionalHubspotFormKey: props.additionalHubspotFormKey,
            couponCode: props.couponCode,
            hubspotFormKey: props.hubspotFormKey,
            productId: effectiveLegacyProductId || props.productId,
            requirePaymentIntent: paymentIsRequired,
            startsAt: props.startsAt,
            stripePaymentIntentId: stripePaymentIntentId,
            stripePaymentIntentReturnUrl: window.location.href,
            stripePaymentMethodId: stripePaymentMethod?.id,
            termId: props.termId,
            ...(variables || {})
          }
        }
      });
    } catch (err) {
      // prettyErrorMessage prefers server payment details (insufficient funds, expired card, etc.)
      setErrorMessage(prettyErrorMessage(err));
      setProcessing(false);
      if (props.afterError) {
        await props.afterError(checkoutResponse, err);
      }
      return;
    }
    if (!checkoutResponse) {
      setErrorMessage('Checkout failed to process. Please try again or contact support@startups.com');
      setProcessing(false);
      if (props.afterError) {
        await props.afterError(checkoutResponse, 'Checkout failed to process. Please try again or contact support@startups.com');
      }
      return;
    }
    if (checkoutResponse?.errors && checkoutResponse.errors.length > 0) {
      setErrorMessage(checkoutResponse.errors.map((e: any) => prettyErrorMessage({
        graphQLErrors: [e]
      })).join('; '));
      setProcessing(false);
      if (props.afterError) {
        await props.afterError(checkoutResponse, checkoutResponse.errors.map((e_0: any) => prettyErrorMessage({
          graphQLErrors: [e_0]
        })).join('; '));
      }
      return;
    }
    const checkoutData: SubmitCheckoutPayload = checkoutResponse?.data?.submitCheckout || {};
    if (checkoutData.subscription?.subscriptionId) {
      await refetchCurrentUser();
      setProcessing(false);
      setSuccess(true);
      try {
        await tracker.orderCompleted({
          subscriptionId: checkoutData?.subscription?.subscriptionId,
          product: quote.product
        });
        await tracker.fgPurchased({
          ...checkoutData.subscription,
          product: checkoutData.subscription?.product
        });
        if (quote?.product?.productId?.includes('investor-match')) {
          await tracker.investorMatchingPurchase({
            coupon: quote?.product?.appliedCoupon?.code || null,
            revenue: quote?.product?.effectivePricingInCents?.signup / 100
          });
        }
      } catch (err_0) {}
      if (props.returnToHref) {
        if (props.afterSuccess) {
          await props.afterSuccess(checkoutResponse?.data?.submitCheckout);
        }
        router.push(props.returnToHref);
      } else {
        if (props.afterSuccess) {
          await props.afterSuccess(checkoutResponse?.data?.submitCheckout);
        }
      }
      return;
    }
    if (!variables && stripeInstance && checkoutData.stripePaymentIntentId) {
      setProcessingMessage('Authorizing Payment Method...');
      const stripeData = JSON.parse(checkoutData.stripeDataAsJson || '{}');
      const paymentIntent = stripeData.paymentIntent;
      if (paymentIntent?.status == 'requires_action' || paymentIntent?.status == 'requires_confirmation') {
        const paymentIntentResult = await stripeInstance.confirmPayment({
          clientSecret: paymentIntent.client_secret,
          confirmParams: {
            return_url: window.location.href
          },
          redirect: 'if_required'
        });
        if (paymentIntentResult.error) {
          // Stripe client messages are already user-friendly (decline reasons, etc.)
          setErrorMessage(paymentIntentResult.error.message || 'Your card was declined. Please try another card or contact your bank.');
          setProcessing(false);
          setSuccess(false);
          if (props.afterError) {
            await props.afterError(checkoutResponse, paymentIntentResult.error.message);
          }
          return;
        } else {
          // Set Payment Intent; this will trigger effect calling submit again
          await handleCheckoutSubmit({
            stripePaymentIntentId: paymentIntentResult.paymentIntent.id,
            stripePaymentMethodId: paymentIntentResult.paymentIntent.payment_method
          });
          return;
        }
      }

      // Soft-returned PI that is not capturable (backend should usually throw instead)
      const lastPaymentError = paymentIntent?.last_payment_error;
      if (lastPaymentError?.message) {
        setProcessing(false);
        setSuccess(false);
        setErrorMessage(lastPaymentError.message);
        if (props.afterError) {
          await props.afterError(checkoutResponse, lastPaymentError.message);
        }
        return;
      }
      setProcessing(false);
      setSuccess(false);
      setErrorMessage(paymentIntent?.status ? `Payment could not be authorized (${paymentIntent.status}). Please try another card.` : 'Payment method is in an invalid state. Please try another card.');
      return;
    }
    setProcessing(false);
    setSuccess(false);
    setErrorMessage('Checkout could not be completed. Please try again or contact support@startups.com');
    return;
  };
  if (quoteQuery.error) {
    return <FormContainer {...props.containerProps || {}}>
        <Center>
          <Heading>Error</Heading>
          <Text>{`${quoteQuery.error}`}</Text>
        </Center>
      </FormContainer>;
  }
  if (!quote || !currentUser) {
    return <FormContainer {...props.containerProps || {}}>
        <VStack align={'center'} gap={'1em'} height={'full'} justify={'center'} marginY={'3em'}>
          <Spinner />
        </VStack>
      </FormContainer>;
  }
  if (processing) {
    return <FormContainer {...props.containerProps || {}}>
        <VStack align={'center'} gap={'1em'} height={'full'} justify={'center'} marginY={'3em'}>
          <Spinner />
          <Heading>{processingMessage}</Heading>
        </VStack>
      </FormContainer>;
  }
  if (success) {
    return <FormContainer height={'full'} {...props.containerProps || {}}>
        <VStack align={'center'} gap={'1em'} height={'full'} justify={'center'} marginY={'3em'}>
          <CheckCircleIcon fontSize={'2em'} opacity={0.5} />
          <Heading>Success!</Heading>
          {!props.afterSuccess && !props.returnToHref && <Button asChild variant={'cta'}>
              <NextLink href={'/onboarding/welcome'} prefetch={false}>
                Proceed to Account Setup
              </NextLink>
            </Button>}
        </VStack>
      </FormContainer>;
  }
  return <>
      <FormContainer {...props.containerProps || {}}>
        {quote.product && <TrackEvent action={'checkoutStarted'} onlyOnScreen={true} traits={{
        product: quote.product,
        intent: props.intent
      }} />}
        {processing && <VStack align={'center'} gap={'2em'} justify={'center'} padding={'2em'}>
            <Spinner />
            <Heading fontSize={'1em'}>Processing Transaction...</Heading>
          </VStack>}
        {errorMessage && <Alert.Root borderRadius={'md'} marginBottom={'1.125em'} status={'error'}>
            <Alert.Indicator />
            <Box marginLeft={'1em'}>{errorMessage}</Box>
          </Alert.Root>}
        <VStack gap={'1.125em'} width={'full'}>
          {!props.hideProductTitle && quote.product.paywallOptions.title && <>
              <Box textAlign={'center'} width={'full'}>
                <Heading fontSize={'1.25em'} lineHeight={1}>
                  {props.productTitle || [quote.product.paywallOptions.title, quote.product.specialTermsNote ? '' : quote.product.effectiveTerm.name].filter(Boolean).join(' - ')}
                </Heading>
                {props.allowProductChange && <Link asChild color={'colorPalette.fg/50'} fontSize={'11px'} variant={'underline'}>
                    <NextLink href={startupsPath(props.productChangePath || `/accelerator/plans`, {
                couponCode: props.couponCode
              })}>
                      Change Plan
                    </NextLink>
                  </Link>}
              </Box>
              <Separator width={'full'} />
            </>}

          {!props.hidePricing && <>
              <Box width={'full'}>
                {!paymentIsRequired ? <>
                    {effectivePricing.trialDays ? <HStack justify={'space-between'} width={'full'}>
                        {effectivePricing.trialDays ? <Text fontWeight={'bold'}>
                            {effectivePricing.trialDays}
                            -Day Trial
                          </Text> : <Text fontWeight={'bold'}>Gifted Account</Text>}

                        <Text>FREE</Text>
                      </HStack> : <>
                        <HStack justify={'space-between'} width={'full'}>
                          <Text fontWeight={'bold'}>
                            {effectivePricing.trialDays ? `${effectivePricing.trialDays}-Day Trial` : 'Due Today'}
                          </Text>
                          {listPricing.signup !== effectivePricing.recurring ? <Text>
                              <chakra.span marginRight={'0.5em'} opacity={'0.75'} textDecoration={'line-through'}>
                                {renderCurrency(listPricing.signup)}
                              </chakra.span>
                              <chakra.span fontWeight={'bold'}>
                                {effectivePricing.signup > 0 ? renderCurrency(effectivePricing.signup) : 'FREE'}
                              </chakra.span>
                            </Text> : <Text>{renderCurrency(effectivePricing.signup)}</Text>}
                        </HStack>
                        {effectivePricing.prepaidPeriods ? <Text fontSize={'14px'} marginTop={'0.25em'} opacity={0.5} width={'full'}>
                            <strong>FREE</strong>
                            {' access for '}
                            {effectivePricing.prepaidPeriods}{' '}
                            {effectivePricing.prepaidPeriods > 1 ? pluralize(effectivePricing.prepaidPeriodsUnit) : effectivePricing.prepaidPeriodsUnit}
                            .
                          </Text> : effectivePricing.recurring > 0 && <Text fontSize={'14px'} marginTop={'0.25em'} opacity={0.5} width={'full'}>
                              {'Renews at '}
                              <strong>{renderCurrency(effectivePricing.recurring, true, false)}</strong>
                              {' every'} {effectiveTerm.everyMonthLabel}
                              {effectivePricing?.nextAssessmentAt ? `, starting on ${moment(effectivePricing.nextAssessmentAt).format('ll')}` : ''}
                              . Cancel anytime.
                            </Text>}
                      </>}
                  </> : <>
                    <HStack justify={'space-between'} width={'full'}>
                      <Text fontWeight={'bold'}>
                        {effectivePricing.trialDays ? `${effectivePricing.trialDays}-Day Trial` : 'Due Today'}
                      </Text>

                      {listPricing.signup !== effectivePricing.signup ? <Text>
                          <chakra.span marginRight={'0.5em'} opacity={'0.75'} textDecoration={'line-through'}>
                            {renderCurrency(listPricing.signup, true, false)}
                          </chakra.span>
                          <chakra.span fontWeight={'bold'}>
                            {effectivePricing.signup == 0 ? 'FREE' : renderCurrency(effectivePricing.signup, true, false)}
                          </chakra.span>
                        </Text> : <Text>{renderCurrency(effectivePricing.signup, true, false)}</Text>}
                    </HStack>
                    {queryParams.get('split') && <Text fontSize={'14px'} marginTop={'0.25em'} opacity={0.5} width={'full'}>
                        {'Additional '}
                        {renderCurrency(effectivePricing.signup, true, false)}
                        {' due on'} {moment().add(1, 'month').format('LL')}
                      </Text>}
                    {quote.product?.effectiveTermId !== 'one' && effectivePricing.recurring > 0 && <Text fontSize={'14px'} marginTop={'0.25em'} opacity={0.5} width={'full'}>
                        {'Renews at '}
                        <strong>{renderCurrency(effectivePricing.recurring, true, false)}</strong>
                        {' every'} {effectiveTerm.everyMonthLabel}
                        {effectivePricing?.nextAssessmentAt ? `, starting on ${moment(effectivePricing.nextAssessmentAt).format('ll')}` : ''}
                        . Cancel anytime.
                      </Text>}
                    {quote.product.specialTermsNote && <Text fontSize={'14px'} marginTop={'0.25em'} opacity={0.5} width={'full'}>
                        {quote.product.specialTermsNote}
                      </Text>}
                  </>}
              </Box>

              <Separator width={'full'} />
            </>}

          {currentUser?.isRegistered ? <>
              {(!currentUser?.fullName || !currentUser?.email || !currentUser?.phone) && !bypassProfileConfirmation ? <UserBasicProfileForm afterSuccess={() => setBypassProfileConfirmation(true)} title={'Confirm Your Info'} buttonProps={{
            size: 'lg',
            title: 'Save & Continue'
          }} containerProps={{
            bgColor: 'transparent',
            border: '0',
            padding: 0,
            boxShadow: 'none'
          }} /> : <>
                  {!quote ? <>
                      <Text>Loading...</Text>
                    </> : <>
                      {!paymentIsRequired ? <>
                          {props.hidePricing && <Heading>No Payment Required!</Heading>}
                          <Button size={'lg'} variant={'cta'} width={'full'} onClick={() => handleCheckoutSubmit({})}>
                            {quote?.product?.effectivePricingInCents?.trialDays ? `Start Your ${quote?.product?.effectivePricingInCents?.trialDays}-Day Trial` : 'Activate Account'}
                            <LuChevronRight />
                          </Button>
                        </> : <>
                          {paymentProvider == 'stripe-methods' && <StripePaymentMethod trialDays={effectivePricing.trialDays} amountsDueInCents={{
                  signup: quote.product.effectivePricingInCents.signup,
                  recurring: quote.product.effectivePricingInCents.recurring
                }} onPaymentMethodReady={paymentMethod => setStripePaymentMethod(paymentMethod)} onStripeReady={stripeInstance_0 => setStripeInstance(stripeInstance_0)} />}
                        </>}
                    </>}
                </>}
            </> : <Box paddingTop={'1em'} width={'full'}>
              {registerFormType == 'login' ? <UserLoginForm inputProps={{
            colorScheme: 'whiteAlpha'
          }} submitButtonProps={{
            variant: 'cta'
          }} title={'Login'} containerProps={{
            bgColor: 'transparent',
            padding: 0,
            border: 0,
            boxShadow: 'none'
          }} onRegisterClick={() => setRegisterFormType('register')} /> : <UserSignupRegisterForm hubspotFormKey={'scom-accelerator-registration'} inputProps={{
            colorScheme: 'whiteAlpha'
          }} showPhoneField={true} title={'Create Your Account'} containerProps={{
            bgColor: 'transparent',
            padding: 0,
            border: 0,
            boxShadow: 'none'
          }} onLoginClick={() => setRegisterFormType('login')} {...props.registerFormProps} />}
            </Box>}
        </VStack>
        {currentUser?.isRegistered && <Text fontSize={'11px'} marginTop={'1em'} opacity={0.5} textAlign={'center'} width={'full'}>
            {'Submission confirms agreement to our'}{' '}
            <Link href={'/legal/terms-of-service'} target={'_blank'} variant={'underline'}>
              Terms of Service
            </Link>
            {quoteQuery.data?.scoPurchaseQuote?.product?.statementOfWorkUrl && <>
                {' '}
                {'and'}{' '}
                <Link href={quoteQuery.data?.scoPurchaseQuote?.product?.statementOfWorkUrl} target={'_blank'} variant={'underline'}>
                  Statement of Work
                </Link>
              </>}
          </Text>}
      </FormContainer>
      {props.showCouponForm && !processing && <Center marginY={'1em'} width={'full'}>
          <CouponForm />
        </Center>}
    </>;
};